import React, { useState, useRef } from 'react';
import AppShell from '../components/AppShell';
import { useAuth } from '../context/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Sparkles, Shirt, Users, Camera, Paintbrush, Wand2, Layers,
  Flame, Snowflake, Zap, Eye, Scissors, Film, Image as ImageIcon,
  ArrowRight, Star, Palette, Upload, Loader2, Download, X, Check
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const APP_CATEGORIES = [
  { key: 'all', label: 'All', icon: Sparkles },
  { key: 'effects', label: 'Visual Effects', icon: Flame },
  { key: 'style', label: 'Style & Fashion', icon: Shirt },
  { key: 'editing', label: 'Editing', icon: Paintbrush },
  { key: 'character', label: 'Character', icon: Users },
  { key: 'marketing', label: 'Marketing', icon: Star },
];

const APPS = [
  { id: 'outfit-swap', name: 'Outfit Swap', desc: 'Change outfits on any character image', icon: Shirt, category: 'style', gradient: 'from-pink-500/20 to-rose-500/20', border: 'border-pink-500/15', placeholder: 'Describe the new outfit...' },
  { id: 'style-snap', name: 'Style Snap', desc: 'Apply artistic styles from reference', icon: Palette, category: 'style', gradient: 'from-violet-500/20 to-purple-500/20', border: 'border-violet-500/15', placeholder: 'Describe the style to apply...' },
  { id: 'face-swap', name: 'Face Swap', desc: 'Swap faces between two images', icon: Users, category: 'character', gradient: 'from-blue-500/20 to-cyan-500/20', border: 'border-blue-500/15', placeholder: 'Describe face blend instructions...' },
  { id: 'skin-enhancer', name: 'Skin Enhancer', desc: 'Natural, realistic skin textures', icon: Eye, category: 'editing', gradient: 'from-amber-500/20 to-orange-500/20', border: 'border-amber-500/15', placeholder: 'Natural glow, clear complexion...' },
  { id: 'bg-remove', name: 'Background Remove', desc: 'Remove or replace background', icon: Scissors, category: 'editing', gradient: 'from-emerald-500/20 to-green-500/20', border: 'border-emerald-500/15', placeholder: 'Describe the new background...' },
  { id: 'angles', name: 'Angles', desc: 'Generate any angle from one image', icon: Camera, category: 'character', gradient: 'from-cyan-500/20 to-blue-500/20', border: 'border-cyan-500/15', placeholder: 'e.g. three-quarter view, bird\'s eye...' },
  { id: 'explosion', name: 'Explosion VFX', desc: 'Add cinematic explosions', icon: Flame, category: 'effects', gradient: 'from-red-500/20 to-orange-500/20', border: 'border-red-500/15', placeholder: 'Describe the explosion effect...' },
  { id: 'freeze', name: 'Freeze Frame', desc: 'Dramatic freeze frame effects', icon: Snowflake, category: 'effects', gradient: 'from-sky-500/20 to-blue-500/20', border: 'border-sky-500/15', placeholder: 'Describe the freeze effect...' },
  { id: 'glitch', name: 'Glitch Effect', desc: 'Digital glitch and distortion', icon: Zap, category: 'effects', gradient: 'from-violet-500/20 to-fuchsia-500/20', border: 'border-violet-500/15', placeholder: 'Describe glitch intensity...' },
  { id: 'upscale', name: 'Upscale', desc: 'Enhance and upscale resolution', icon: Wand2, category: 'editing', gradient: 'from-teal-500/20 to-emerald-500/20', border: 'border-teal-500/15', placeholder: 'Maximum detail and sharpness...' },
  { id: 'headshot', name: 'AI Headshot', desc: 'Professional headshots from any photo', icon: Camera, category: 'character', gradient: 'from-indigo-500/20 to-violet-500/20', border: 'border-indigo-500/15', placeholder: 'Confident expression, gray background...' },
  { id: 'relight', name: 'Relight', desc: 'Change lighting on any image', icon: Star, category: 'editing', gradient: 'from-yellow-500/20 to-amber-500/20', border: 'border-yellow-500/15', placeholder: 'e.g. golden hour, dramatic side light...' },
  { id: 'color-grade', name: 'Color Grading', desc: 'Professional color grading presets', icon: Paintbrush, category: 'editing', gradient: 'from-orange-500/20 to-red-500/20', border: 'border-orange-500/15', placeholder: 'e.g. teal & orange, vintage film...' },
  { id: 'product-shot', name: 'Product Shot', desc: 'Premium product photography', icon: ImageIcon, category: 'marketing', gradient: 'from-emerald-500/20 to-teal-500/20', border: 'border-emerald-500/15', placeholder: 'Describe the product setting...' },
  { id: 'billboard', name: 'Billboard Ad', desc: 'Put your product on a billboard', icon: Film, category: 'marketing', gradient: 'from-amber-500/20 to-yellow-500/20', border: 'border-amber-500/15', placeholder: 'e.g. Times Square, highway, rooftop...' },
  { id: 'transitions', name: 'Transitions Pack', desc: 'Smooth cinematic transitions', icon: Layers, category: 'effects', gradient: 'from-fuchsia-500/20 to-pink-500/20', border: 'border-fuchsia-500/15', placeholder: 'Describe transition style...', noImage: true },
];

export default function AppsPage() {
  const { token } = useAuth();
  const [activeCategory, setActiveCategory] = useState('all');
  const [selectedApp, setSelectedApp] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [prompt, setPrompt] = useState('');
  const [uploadedFile, setUploadedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [running, setRunning] = useState(false);
  const [resultImage, setResultImage] = useState(null);
  const fileRef = useRef(null);

  const filteredApps = activeCategory === 'all' ? APPS : APPS.filter(a => a.category === activeCategory);
  const headers = { Authorization: `Bearer ${token}` };

  const openApp = (app) => {
    setSelectedApp(app);
    setPrompt('');
    setUploadedFile(null);
    setPreviewUrl(null);
    setResultImage(null);
    setDialogOpen(true);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
  };

  const pollJob = async (jobId) => {
    for (let i = 0; i < 120; i++) {
      await new Promise(r => setTimeout(r, 3000));
      try {
        const { data } = await axios.get(`${API}/apps/jobs/${jobId}`);
        if (data.status === 'completed') return data.result;
        if (data.status === 'failed') throw new Error(data.error || 'Generation failed');
      } catch (err) {
        if (err.message !== 'Generation failed') continue;
        throw err;
      }
    }
    throw new Error('Timed out');
  };

  const handleRun = async () => {
    if (!selectedApp) return;
    const needsImage = !selectedApp.noImage;
    if (needsImage && !uploadedFile) return toast.error('Upload an image to get started');

    setRunning(true);
    setResultImage(null);
    try {
      const formData = new FormData();
      formData.append('app_id', selectedApp.id);
      formData.append('prompt', prompt);
      if (uploadedFile) formData.append('image', uploadedFile);

      const { data } = await axios.post(`${API}/apps/run`, formData, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' },
      });
      toast.info(`Running ${selectedApp.name}...`);

      const result = await pollJob(data.job_id);
      setResultImage(result.image_url);
      toast.success(`${selectedApp.name} complete!`);
    } catch (err) {
      toast.error(err.message || 'App failed');
    } finally {
      setRunning(false);
    }
  };

  const downloadResult = () => {
    if (!resultImage) return;
    const link = document.createElement('a');
    link.href = resultImage;
    link.download = `${selectedApp?.id || 'result'}_output.png`;
    link.target = '_blank';
    link.click();
  };

  return (
    <AppShell>
      <div className="min-h-screen bg-[#0a0a0f] text-white" data-testid="apps-page">
        {/* Header */}
        <div className="border-b border-white/[0.06] bg-[#0d0d14]/80 backdrop-blur-md">
          <div className="max-w-7xl mx-auto px-6 py-6">
            <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="apps-title">Apps</h1>
            <p className="text-sm text-white/40 mt-1">One-click creative tools powered by AI</p>
          </div>
        </div>

        {/* Category Tabs */}
        <div className="max-w-7xl mx-auto px-6 pt-5">
          <div className="flex gap-1.5 overflow-x-auto pb-2 scrollbar-none" data-testid="apps-categories">
            {APP_CATEGORIES.map(cat => (
              <button key={cat.key}
                data-testid={`category-${cat.key}`}
                onClick={() => setActiveCategory(cat.key)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
                  activeCategory === cat.key
                    ? 'bg-white/[0.08] text-white'
                    : 'text-white/35 hover:text-white/60 hover:bg-white/[0.03]'
                }`}>
                <cat.icon className="w-4 h-4" />
                {cat.label}
              </button>
            ))}
          </div>
        </div>

        {/* Apps Grid */}
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {filteredApps.map(app => (
              <button key={app.id}
                data-testid={`app-${app.id}`}
                onClick={() => openApp(app)}
                className={`group text-left rounded-xl border ${app.border} bg-gradient-to-br ${app.gradient} p-5 hover:scale-[1.02] transition-all duration-200`}>
                <app.icon className="w-7 h-7 text-white/30 mb-3 group-hover:text-white/60 transition-colors" />
                <h3 className="text-sm font-semibold text-white/80 mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{app.name}</h3>
                <p className="text-[11px] text-white/30 leading-relaxed">{app.desc}</p>
                <div className="flex items-center gap-1 mt-3 text-[10px] text-white/20 group-hover:text-white/40 transition-colors">
                  Open <ArrowRight className="w-3 h-3" />
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* App Runner Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="bg-[#12121a] border-white/[0.08] text-white max-w-2xl p-0 overflow-hidden">
            {selectedApp && (
              <>
                {/* App Header */}
                <div className={`px-6 pt-6 pb-4 bg-gradient-to-br ${selectedApp.gradient}`}>
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-3">
                      <selectedApp.icon className="w-6 h-6 text-white/60" />
                      <span style={{ fontFamily: 'Outfit, sans-serif' }}>{selectedApp.name}</span>
                    </DialogTitle>
                  </DialogHeader>
                  <p className="text-xs text-white/40 mt-1">{selectedApp.desc}</p>
                </div>

                <div className="p-6 space-y-4">
                  {/* Result Display */}
                  {resultImage && (
                    <div className="relative rounded-xl overflow-hidden border border-white/[0.08] bg-black">
                      <img src={resultImage} alt="Result" className="w-full max-h-[400px] object-contain" data-testid="app-result-image" />
                      <div className="absolute top-2 right-2 flex gap-2">
                        <Button size="sm" onClick={downloadResult} className="bg-black/60 backdrop-blur-md hover:bg-black/80 gap-1.5">
                          <Download className="w-3.5 h-3.5" /> Save
                        </Button>
                      </div>
                      <div className="absolute bottom-2 left-2">
                        <span className="px-2 py-0.5 bg-emerald-500/20 border border-emerald-500/30 rounded-full text-[10px] text-emerald-400 flex items-center gap-1">
                          <Check className="w-2.5 h-2.5" /> Complete
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Image Upload (if needed) */}
                  {!selectedApp.noImage && (
                    <div>
                      <Label className="text-xs text-white/50 mb-2 block">Upload Image</Label>
                      <input type="file" ref={fileRef} accept="image/*" onChange={handleFileSelect} className="hidden" data-testid="app-file-input" />
                      {previewUrl ? (
                        <div className="relative rounded-lg overflow-hidden border border-white/[0.08] bg-black/40">
                          <img src={previewUrl} alt="Upload" className="w-full max-h-[200px] object-contain" />
                          <button onClick={() => { setUploadedFile(null); setPreviewUrl(null); if (fileRef.current) fileRef.current.value = ''; }}
                            className="absolute top-2 right-2 p-1.5 bg-black/60 rounded-md hover:bg-red-500/30 transition-colors">
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ) : (
                        <button onClick={() => fileRef.current?.click()} data-testid="app-upload-btn"
                          className="w-full h-28 rounded-xl border-2 border-dashed border-white/[0.08] bg-white/[0.02] hover:border-white/[0.15] hover:bg-white/[0.04] transition-all flex flex-col items-center justify-center gap-2">
                          <Upload className="w-6 h-6 text-white/20" />
                          <span className="text-xs text-white/30">Click to upload image</span>
                        </button>
                      )}
                    </div>
                  )}

                  {/* Prompt */}
                  <div>
                    <Label className="text-xs text-white/50 mb-1 block">Prompt (optional)</Label>
                    <Textarea data-testid="app-prompt"
                      value={prompt} onChange={e => setPrompt(e.target.value)}
                      placeholder={selectedApp.placeholder || 'Describe what you want...'}
                      className="bg-white/[0.04] border-white/[0.08] text-white min-h-[70px] placeholder:text-white/20"
                    />
                  </div>

                  {/* Run Button */}
                  <Button data-testid="app-run-btn" onClick={handleRun} disabled={running || (!selectedApp.noImage && !uploadedFile)}
                    className="w-full bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-400 hover:to-red-500 h-11 gap-2 text-sm font-semibold">
                    {running ? <><Loader2 className="w-4 h-4 animate-spin" /> Running {selectedApp.name}...</> : <><Wand2 className="w-4 h-4" /> Run {selectedApp.name}</>}
                  </Button>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AppShell>
  );
}
